<script setup lang="ts">
import { computed } from 'vue';
import {
  any, array, bool, func, object, string,
} from 'vue-types';
import { Button } from 'ant-design-vue';

import TableEvent from './TableEvent.vue';

import ConfirmDelete from '@/ui/ConfirmDelete.vue';
import AgentLinkLoad from '@/components/Agents/Link/AgentLinkLoad.vue';
import PortalContactLoad from '@/components/PartnerPortal/Contact/Link/PortalContactLoad.vue';
import useLocalProvider from '@/providers/useLocalProvider';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Any = any;
const props = defineProps({
  column: string().isRequired,
  record: object().isRequired,
  getLink: func<(data: Any) => string>(),
  onClick: func<(data: Any) => void>(),
  onSelect: func<(data: Any) => void>(),
  onSelectTitle: string(),
  onDelete: func<(data: Any) => void>(),
  deleteLoading: bool(),
  class: any(),
  extraDateCols: array<string>().def([]),
  extraDateTimeCols: array<string>().def([]),
  extraAgentCols: array<string>().def([]),
  getFormat: func<(value: Any) => void>(),
});

const { dateShortTimeLocal, dateShort } = useLocalProvider();

const dateTimeColumns: string[] = [
  'last_update_time',
  'creation_time',
  'created_at',
  'updated_at',
  ...props.extraDateTimeCols,
];
const columnFormat = computed(() => {
  const value = props.record[props.column];
  if (dateTimeColumns.includes(props.column) && value) {
    return dateShortTimeLocal(value);
  }
  if (props.extraDateCols.includes(props.column) && value) {
    return dateShort(value);
  }
  return props.getFormat ? props.getFormat(value) : value;
});

const agentColumns: string[] = [
  'last_update_by',
  'created_by',
  ...props.extraAgentCols,
];
</script>

<template>
  <Button
    v-if="column === 'onSelect' && onSelect"
    data-test-id="id-on-select"
    @click="() => onSelect ? onSelect(record) : undefined"
  >
    {{ onSelectTitle ?? 'Select' }}
  </Button>
  <ConfirmDelete
    v-else-if="column === 'onDelete' && onDelete"
    :on-confirm="() => onDelete ? onDelete(record) : undefined"
    :loading="deleteLoading"
  />
  <template v-else-if="agentColumns.includes(column)">
    <template v-if="['last_update_time', 'creation_time'].includes(column)">
      <AgentLinkLoad v-if="record[`${column}_type`] === 'AGENT'" :id="record[column]" />
      <PortalContactLoad v-else-if="record[`${column}_type`] === 'PORTALC'" :id="record[column]" />
    </template>
    <template v-else>
      <AgentLinkLoad v-if="record[column]" :id="record[column]" />
      <slot v-else v-bind="{ value: columnFormat, column }" />
    </template>
  </template>
  <TableEvent
    v-else
    :record="record"
    :get-link="getLink"
    :on-click="onClick"
    :class="[onClick ? 'ant-btn-link' : '', $props.class ?? 'text-left']"
    :data-test-id="`id-${column}`"
  >
    <slot v-bind="{ value: columnFormat, column }">
      {{ columnFormat }}
    </slot>
  </TableEvent>
</template>
